import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Heart } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import MicButton from "@/components/MicButton";

const USE_AGAIN = ["Definitely", "Probably", "Probably not", "No"];

export default function FeedbackModal({ onClose }) {
  const [helped, setHelped] = useState("");
  const [confusing, setConfusing] = useState("");
  const [change, setChange] = useState("");
  const [wouldUseAgain, setWouldUseAgain] = useState("");
  const [other, setOther] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!helped.trim() && !confusing.trim() && !change.trim() && !wouldUseAgain && !other.trim()) {
      toast.error("Even a few words help — tell us anything!");
      return;
    }
    setSending(true);
    try {
      await api.post("/feedback", {
        helped,
        confusing,
        change,
        would_use_again: wouldUseAgain,
        other,
        page: typeof window !== "undefined" ? window.location.pathname : "",
      });
      toast.success("Thank you — truly. Your feedback helps us build Between Us.");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSending(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-[#2D2A26]/40 backdrop-blur-sm"
        onClick={onClose}
        data-testid="feedback-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-xl h-full sm:h-auto sm:max-h-[92vh] overflow-y-auto bg-[#FDFBF7] sm:rounded-2xl border border-[#E8E3D9] shadow-xl"
          data-testid="feedback-modal"
        >
          <div className="sticky top-0 flex items-center justify-between px-6 py-3.5 border-b border-[#E8E3D9] bg-[#FDFBF7]/95 backdrop-blur">
            <p className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-[#4A5D4E]">
              <Heart className="h-4 w-4" strokeWidth={1.75} /> We want your feedback
            </p>
            <button onClick={onClose} data-testid="feedback-close" className="p-2 rounded-full text-[#6E6860] hover:bg-[#F5F2EA] transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-6 sm:px-8 py-6">
            <h2 className="font-serif text-2xl sm:text-3xl text-[#2D2A26]">Please… PLEASE! Help us improve.</h2>
            <div className="mt-3 space-y-2 text-[#6E6860] leading-relaxed text-sm">
              <p>
                Between Us is still <span className="font-medium text-[#2D2A26]">under active development</span>, and we're
                learning as we go. Your experience can genuinely shape what it becomes.
              </p>
              <p>
                We'd love to hear what worked, what didn't, and what we should change. Every suggestion, criticism, and
                new idea helps us build a better Between Us for the next couple. Even a few words make a real difference.
              </p>
            </div>

            <div className="mt-6 space-y-5">
              <Field label="1. What helped you most?" value={helped} onChange={setHelped} testId="feedback-helped" />
              <Field label="2. What was confusing, frustrating, or unhelpful?" value={confusing} onChange={setConfusing} testId="feedback-confusing" />
              <Field label="3. If you could change one thing about Between Us, what would it be?" value={change} onChange={setChange} testId="feedback-change" />

              <div>
                <label className="block text-sm font-medium text-[#2D2A26] mb-2">4. Would you use Between Us again?</label>
                <div className="flex flex-wrap gap-2" role="radiogroup">
                  {USE_AGAIN.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setWouldUseAgain(opt)}
                      data-testid={`feedback-use-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                      aria-pressed={wouldUseAgain === opt}
                      className={`px-4 py-2 rounded-full text-sm border transition-all ${
                        wouldUseAgain === opt
                          ? "bg-[#4A5D4E] text-[#FDFBF7] border-[#4A5D4E]"
                          : "bg-white text-[#2D2A26] border-[#E8E3D9] hover:border-[#4A5D4E]/50"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <Field label="Anything else you'd like to tell us?" hint="(optional)" value={other} onChange={setOther} testId="feedback-other" />
            </div>

            <div className="mt-7 flex items-center gap-4">
              <button
                onClick={submit}
                disabled={sending}
                data-testid="feedback-submit"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#4A5D4E] text-[#FDFBF7] font-medium hover:bg-[#3B4A3E] transition-all hover:-translate-y-0.5 disabled:opacity-60"
              >
                <Send className={`h-4 w-4 ${sending ? "animate-pulse" : ""}`} /> {sending ? "Sending…" : "Send feedback"}
              </button>
              <button onClick={onClose} data-testid="feedback-later" className="text-sm text-[#6E6860] hover:text-[#2D2A26] transition-colors">
                Maybe later
              </button>
            </div>
            <p className="mt-4 text-xs text-[#A39E94]">Thank you for helping us build Between Us.</p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Field({ label, hint, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#2D2A26] mb-2">
        {label} {hint && <span className="text-[#A39E94] font-normal">{hint}</span>}
      </label>
      <div className="relative">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          data-testid={testId}
          className="w-full rounded-xl border border-[#E8E3D9] bg-white px-4 py-3 pr-12 text-[#2D2A26] placeholder:text-[#A39E94] focus:ring-2 focus:ring-[#4A5D4E] focus:outline-none transition resize-none"
        />
        <MicButton value={value} onChange={onChange} className="absolute bottom-2.5 right-2.5" testId={`${testId}-mic`} />
      </div>
    </div>
  );
}
